import {createAsyncThunk} from '@reduxjs/toolkit';
import moment from 'moment';
import {
  Filters,
} from '../../../implementation-specialist/models/reportsFilters';
import {
  showErrorStatus,
} from '../../../security/state/user-slice';
import {ApiBatchLimits, reportsType} from '../../../shared/enums';
import {
  allInstanceOfCareFilters,
  automatedPaymentFilters,
  mergedTransactionsFilters,
  patientFinancialClearenceFilters,
  pesActivityLogReportFilters,
  riskClassReportFilters,
  settlementDetailFilters,
  settlementSummaryV2Filters,
  summaryTransactionsFilters,
} from '../../../shared/Mocks/reportsFilters';
import {getReportsName} from '../../../shared/Mocks/reportsType';
import {AxiosResultStatus} from '../../../shared/service/axios-result-status';
import {emptyReportInfo, ReportsInfo} from '../../models/reports-info';
import {userService} from '../../services/admin-user-service';

const robustErrorHandler = (
    error: {
      entity: { code: string; message: string };
      errorMessage: string;
      hasErrors: boolean;
    },
    thunkAPI: any
) => {
  const { entity, errorMessage } = error;
if (
    (entity?.code !== "INTERNAL_SERVER_ERROR" && entity?.message) ||
    entity?.message
) {
  // handle external error
  thunkAPI.dispatch(showErrorStatus(entity?.message));
  throw new Error(entity?.message);
} else {
  // handle internal error
  thunkAPI.dispatch(showErrorStatus(errorMessage));
  throw new Error(errorMessage);
}
}


export const handleGetFilteredReports = createAsyncThunk("handleGetFilteredReports", async (reportsFilters: Filters,thunkAPI) => {
  let offset = 0;
  let limit = ApiBatchLimits.reports;
  let reportTypeValues: Array<Object> = []
  let response: AxiosResultStatus;
  let reportFiltered:ReportsInfo = emptyReportInfo;
  let data: string[][] = []
  if (reportsFilters.startDate) {
    const startDate = moment(reportsFilters.startDate).startOf('day').utc();
    reportsFilters.startDate = startDate.toISOString();
  }
  if (reportsFilters.endDate) {
    const endDate = moment(reportsFilters.endDate).endOf('day').utc();
    reportsFilters.endDate = endDate.toISOString();
  }
  if (reportsFilters.admissionDate) {
    const admissionDate = new Date(reportsFilters.admissionDate || '').setUTCHours(23,59,59,999);
    reportsFilters.admissionDate = new Date(admissionDate || '').toISOString();
  }
  if (reportsFilters.dischargeDate) {
    const dischargeDate = new Date(reportsFilters.dischargeDate || '').setUTCHours(23,59,59,999);
    reportsFilters.dischargeDate = new Date(dischargeDate || '').toISOString();
  }

  switch (reportsFilters.reportType) {
    case reportsType.AllInstanceofCare:
      reportTypeValues = allInstanceOfCareFilters
      limit = ApiBatchLimits.allInstanceofCareReports
      break;

    case reportsType.PatientFinancialClearance:
      reportTypeValues = patientFinancialClearenceFilters
      break;

    case reportsType.SettlementDetail:
      reportTypeValues = settlementDetailFilters
      break;

    case reportsType.RiskClassReports:
      reportTypeValues = riskClassReportFilters
      break;

    case reportsType.PESActivityLog:
      reportTypeValues = pesActivityLogReportFilters
      limit = ApiBatchLimits.pesActivityLogReports;
      break;

    case reportsType.SummaryTransactions:
      reportTypeValues = summaryTransactionsFilters
      break;

    case reportsType.MergedTransactions:
      reportTypeValues = mergedTransactionsFilters
      limit = ApiBatchLimits.mergedTransactionsReports;
      break;

    case reportsType.AutomatedPayment:
      reportTypeValues = automatedPaymentFilters;
      limit = ApiBatchLimits.automatedPaymentReports;
      break;

    case reportsType.SettlementSummary:
      reportTypeValues = settlementSummaryV2Filters;
      limit = ApiBatchLimits.settlementSummaryReports;
      break;

    default:
      break;
  }
  reportsFilters.reportType = reportsType[reportsFilters.reportType]
  const reportName = getReportsName(reportsFilters.reportType)

  do {
    response = await userService.getReportsInfo(reportsFilters, offset, limit);
    if (response.hasErrors) {
      robustErrorHandler(response, thunkAPI);
    }

    reportFiltered = response.entity
    data = [...data, ...response.entity.data]
    offset = offset + limit;
    //need to set >= as opposed to === because for PES activity log there are multiple report entries per one query result
  } while (response.entity.data.length >= limit);

  reportFiltered.data = data
  reportFiltered.isInitialCall = true

  if (reportFiltered.data.length === 0){
    thunkAPI.dispatch(
      showErrorStatus(
        `No ${reportName?.toLowerCase()?.includes('report') ? reportName : reportName + ' reports'} found for filter criteria entered`
      )
    )
  }

  return {reportFiltered,reportTypeValues}
});

export const handleGetDetailedFilteredReports = createAsyncThunk("handleGetDetailedFilteredReports", async (detailedReportFilters: any,thunkAPI) => {
  let offset = 0;
  const limit = ApiBatchLimits.reports;
  let response: AxiosResultStatus;
  let reportFiltered = emptyReportInfo;
  let data: string[][] = []
  detailedReportFilters.startDate= new Date(detailedReportFilters.startDate||'').toISOString()
  const endDate = new Date(detailedReportFilters.endDate || '').setUTCHours(23,59,59,999);
  detailedReportFilters.endDate= new Date(endDate || '').toISOString();

  if (detailedReportFilters.admissionDate) {
    const admissionDate = new Date(detailedReportFilters.admissionDate || '').setUTCHours(23,59,59,999);
    detailedReportFilters.admissionDate = new Date(admissionDate || '').toISOString();
  }

  if (detailedReportFilters.dischargeDate) {
    const dischargeDate = new Date(detailedReportFilters.dischargeDate || '').setUTCHours(23,59,59,999);
    detailedReportFilters.dischargeDate = new Date(dischargeDate || '').toISOString();
  }

  let reportType

  for (const [key, value] of Object.entries(reportsType)) {
    if(value === detailedReportFilters.reportType){
      reportType = key
    }
  }
  detailedReportFilters.reportType = reportType
  const reportName = getReportsName(detailedReportFilters.reportType)

  do {
    response = await userService.getReportsInfo(detailedReportFilters, offset, limit);

    if (response.hasErrors) {
      robustErrorHandler(response, thunkAPI);
    }

    reportFiltered = response.entity
    data = [...data, ...response.entity.data]
    offset = offset + limit;
  } while (response.entity.data.length === limit);

  reportFiltered.data = data
  reportFiltered.isInitialCall = false;

  if (reportFiltered.data.length === 0){
    thunkAPI.dispatch(
      showErrorStatus(
        `No ${reportName?.toLowerCase()?.includes('report') ? reportName : reportName + ' reports'} found for filter criteria entered`
        )
      )
  }

   return reportFiltered
});

export const handleGetDetailedSettlementReport = createAsyncThunk("handleGetDetailedSettlementReport", async (detailedReportFilters: any,thunkAPI) => {
  let offset = 0;
  const limit = ApiBatchLimits.reports;
  let response: AxiosResultStatus;
  let reportFiltered = emptyReportInfo;
  let data: string[][] = []
  detailedReportFilters.startDate= new Date(detailedReportFilters.startDate||'').toISOString()
  const endDate = new Date(detailedReportFilters.endDate || '').setUTCHours(23,59,59,999);
  detailedReportFilters.endDate= new Date(endDate || '').toISOString();
  let reportType

  for (const [key, value] of Object.entries(reportsType)) {
    if(value === detailedReportFilters.reportType){
      reportType = key
    }
  }
  detailedReportFilters.reportType = reportType
  const reportName = getReportsName(detailedReportFilters.reportType)

  do {
    response = await userService.getReportsInfo(detailedReportFilters, offset, limit);
    if (response.hasErrors) {
      robustErrorHandler(response, thunkAPI);
    }
    reportFiltered = response.entity
    data = [...data, ...response.entity.data]
    offset = offset + limit;
  } while (response.entity.data.length === limit);

  reportFiltered.data = data
  reportFiltered.isInitialCall = false;

  if (reportFiltered.data.length === 0){
    thunkAPI.dispatch(
      showErrorStatus(
        `No ${reportName?.toLowerCase()?.includes('report') ? reportName : reportName + ' reports'} found for filter criteria entered`
        )
      )
  }

   return reportFiltered
});


export const getPaymentChannels = createAsyncThunk(
  "getPaymentChannels",
  async () => {
    return await userService.getPaymentChannels();
  }
)


export const getv2Reports = createAsyncThunk(
  "getv2Reports",
  async (dashboardId: number) => {
    return await userService.getv2Reports(dashboardId);
  }
)

export const getAllv2Reports = createAsyncThunk(
  "getAllv2Reports",
  async () => {
    return await userService.getAllv2Reports();
  }
)
